import React from "react";
import { Link } from "react-router-dom";

const TermsOfUsePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="py-5 px-6 md:px-12 border-b border-white/[0.08]">
        <div className="max-w-4xl mx-auto flex items-center">
          <Link to="/" className="flex items-center">
            <img src="/favicon white.png" alt="Plutus" className="h-8 w-8 mr-2 animate-logo-spin" />
            <img src="/dark mode text.png" alt="Plutus" className="h-8 w-auto" />
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06] p-6 sm:p-8 md:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">TERMS OF USE</h1>
          <p className="text-gray-500 mb-8">Effective as of December 22nd, 2025</p>

          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-6">
              These Terms of Use ("Terms") represent a contract between you and Namsoft Advisors, LLC and our affiliates
              ("Namsoft", "we", "us" or "our") governing your use of the Namsoft website and online platform ("Platform")
              and any advisory, administrative, operational, and reporting services made available by Namsoft through the
              Platform from time-to-time (the "Services").
            </p>

            <p className="text-gray-300 mb-4">You must be at least eighteen (18) years old to use the Services.</p>

            <p className="text-gray-300 mb-4">
              As a condition of your use of the Platform, you agree to comply with all of the terms and conditions in
              these Terms at all times. The terms include any Terms to resolve disputes by arbitration on an individual
              basis.
            </p>

            <p className="text-gray-300 mb-4">
              You also agree to comply with the following policies and agreements, which are attached as exhibits and
              incorporated into these Terms:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-1">
              <li>Acceptable Use Policy (Exhibit A)</li>
              <li>Consent to Receive Electronic Disclosures (Exhibit B)</li>
            </ul>

            <p className="text-gray-300 mb-4">
              You also acknowledge receipt of the Namsoft Privacy Policy which is attached as Exhibit C to these Terms.
            </p>

            <p className="text-gray-300 mb-4">
              In order to gain access to the Namsoft investment advisory services, you will also need to execute an
              Investment Advisory Agreement with Namsoft which governs the advisory relationship between you and Namsoft.
              To the extent of any conflict between the terms of such Investment Advisory Agreement and the terms of these
              Terms, the terms of the Investment Advisory Agreement will control.
            </p>

            <p className="text-gray-300 mb-4">
              You will also be subject to any agreements and terms of use or similar documents made available to you by
              third parties that provide services in relation to your Namsoft account and the Services, and you agree to
              abide by all such agreements and terms of use.
            </p>

            <p className="text-gray-300 mb-6">
              We may revise these Terms and any/or of the policies and agreements listed above from time to time. The
              revised version will be effective at the time we post it, unless otherwise noted. We reserve the right to
              amend these Terms at any time without notice, subject to applicable law. By continuing to use our services
              after any changes to these Terms become effective, you agree to abide and be bound by those changes. If you
              do not agree with any changes to these Terms, you must close your Namsoft account immediately.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Opening a Namsoft account</h2>
            <p className="text-gray-300 mb-4">Accounts for individuals are intended solely for the personal use of such individuals.</p>
            <p className="text-gray-300 mb-4">
              In order to open an account, we may, in our discretion, require submission of information to verify the
              identity of the user for business or regulatory purposes. You consent to our sharing of information with
              third parties in order to verify your identity.
            </p>
            <p className="text-gray-300 mb-4">
              All information that you provide must be complete, accurate and truthful. You must update this information
              whenever it changes.
            </p>
            <p className="text-gray-300 mb-4">
              You authorize us to make inquiries, whether directly or through third parties, that we consider necessary to
              verify your identity or to protect you and/or us against fraud, money laundering, terrorist financing or
              other financial crime, and to take any action we deem necessary based on the results of such inquiries.
            </p>
            <p className="text-gray-300 mb-4">
              When we carry out inquiries, you acknowledge and understand that your personal data may be disclosed to
              identity verification, compliance data recordation, credit reference, fraud prevention, or financial crime
              agencies and that these agencies may respond to our inquiries in full. You can review our Privacy Notice to
              obtain more information about how we process your personal data.
            </p>
            <p className="text-gray-300 mb-4">
              We may also require you to submit additional information about yourself, provide additional records or
              documentation, or have face to face meetings with representatives of Namsoft.
            </p>
            <p className="text-gray-300 mb-4">
              Until your information and identity has been verified, you will not be able to access Namsoft's investment
              advisory services.
            </p>
            <p className="text-gray-300 mb-4">
              We reserve the right in our sole and absolute discretion to deny you the right to open an account with
              Namsoft.
            </p>
            <p className="text-gray-300 mb-4">
              However, even after we open an account for you, we reserve the right to close, suspend, or limit access to
              your Namsoft account and/or the Services in the event that, after reasonable enquiries, we are unable to
              obtain information about you required to verify your identity.
            </p>
            <p className="text-gray-300 mb-4">
              Entity accounts are intended solely for the use of the entity named on the account. The person opening or
              accessing an entity account represents and warrants that:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>The entity is duly organized, validly existing, and in good standing under the laws of its jurisdiction of organization;</li>
              <li>The person opening the account has full power and authority to open and operate the account on behalf of the entity and to bind the entity to these Terms;</li>
              <li>The opening and operation of the account has been duly authorized by all necessary corporate, partnership, or other entity action;</li>
              <li>All information provided regarding the entity, its beneficial owners, and authorized representatives is complete, accurate, and truthful.</li>
            </ul>
            <p className="text-gray-300 mb-4">
              We may require you to provide documentation to verify the entity's existence, authority, and beneficial
              ownership, including but not limited to: articles of incorporation or organization, operating agreements,
              partnership agreements, corporate resolutions, certificates of good standing, employer identification
              numbers, and government-issued identification for beneficial owners and authorized signatories.
            </p>
            <p className="text-gray-300 mb-4">
              You must provide information regarding all beneficial owners of the entity as required by law and our
              policies, and immediately notify us of any changes to beneficial ownership or authorized signatories.
            </p>
            <p className="text-gray-300 mb-6">
              The entity and each person acting on its behalf agrees to indemnify and hold harmless Namsoft from any
              claims arising from unauthorized account access or actions taken by persons purporting to act on behalf of
              the entity.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Accessing your Namsoft account</h2>
            <p className="text-gray-300 mb-4">
              To access your Namsoft account, you must have the necessary equipment (such as a computer or smartphone) and
              access to the internet. Your Namsoft account can be accessed directly using the Platform or by such other
              mode of access (including APIs) as we may prescribe from time-to-time.
            </p>
            <p className="text-gray-300 mb-4">
              You are responsible for obtaining the data network access necessary to use the Services. You are responsible
              for acquiring and updating compatible hardware or devices necessary to access and use the Services and the
              Platform. Namsoft does not guarantee that the Namsoft Services, or any portion thereof, will function on any
              particular hardware or devices. The Services may be subject to malfunctions and delays inherent in the use of
              the internet and electronic communications.
            </p>
            <p className="text-gray-300 mb-4">
              You are only permitted to access your Namsoft account by using the Access credentials we provide to you for
              such purposes. We may require multi-factor authentication to keep your Namsoft account safe and secure.
            </p>
            <p className="text-gray-300 mb-4">
              You must ensure that any Namsoft account registered under your name will not be used by any person other
              than yourself, other than in accordance with these Terms.
            </p>
            <p className="text-gray-300 mb-4">
              You are responsible for taking appropriate action to protect your hardware and data from viruses and
              malicious software, and any inappropriate material. Except as provided by applicable law, you are responsible
              for backing up and maintaining duplicate copies of any information you store or transfer through the Namsoft
              Services. We are not responsible for any claim or Losses (as defined below) resulting from your failure to
              comply with this provision.
            </p>
            <p className="text-gray-300 mb-4">
              At all times, you shall maintain adequate security and control of all of your Access credentials. You are
              responsible for taking the necessary security measures to protect your Namsoft account to keep your Access
              credential secure, including by:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>strictly abiding by all of our policies and procedures;</li>
              <li>creating a strong password and maintaining security and control of your Access credentials;</li>
              <li>keeping the email account and telephone number provided to us up to date in order to receive any notices or alerts that we may send you;</li>
              <li>never allowing remote access or sharing your computer and/or computer screen with someone else when you are logged on to your Namsoft account;</li>
              <li>remembering that under no circumstances will we ask you to share your passwords or 2-factor authentication codes; and</li>
              <li>logging out from the website or the Platform at the end of each visit.</li>
            </ul>
            <p className="text-gray-300 mb-4">
              You must keep the email account and Access credentials secure against any attacks and unauthorized access.
              You must notify us immediately if you have knowledge or have reason for suspecting that the security of your
              email account has been compromised or if there has been any unauthorized use of your email account.
            </p>
            <p className="text-gray-300 mb-4">
              It is important that you monitor your Account history to ensure any unauthorized or suspicious activity on
              your Namsoft account is identified and notify us immediately upon discovery of any unauthorized or
              suspicious activity. You acknowledge that any security breach may result in unauthorized access to your
              Namsoft account by third parties.
            </p>
            <p className="text-gray-300 mb-4">If you suspect a security breach, you must ensure that:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>
                we are notified immediately at{" "}
                <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                  hello@runplutus.com
                </a>{" "}
                and continue to be provided with accurate and up to date information throughout the duration of the
                security breach; and
              </li>
              <li>
                you take any other steps that we may reasonably require to reduce, manage or report any security breach.
              </li>
            </ul>
            <p className="text-gray-300 mb-6">
              We reserve the right to request, and you agree to provide, any and all information and documents we deem
              relevant or necessary in connection with an actual or suspected security breach, and may provide such
              information to any third party that we deem necessary in order to resolve any security breach.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Review of Account Activity</h2>
            <p className="text-gray-300 mb-4">
              We may review account activity at various times. This review checks for, among other things, suspicious or
              illegal activity and whether your account complies with these Terms. In connection with our review process,
              you may be required to provide us with additional information and/or documentation to verify your identity.
            </p>
            <p className="text-gray-300 mb-6">Reviews may result in account limitation, suspension or termination.</p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Use of Cookies and Tracking Technologies</h2>
            <p className="text-gray-300 mb-4">
              When you access or use the Platform and Services, we and our third-party service providers may use cookies,
              web beacons, pixel tags, and other tracking technologies (collectively, "Cookies") to recognize you and/or
              your device(s) and to collect information about your use of the Platform and Services.
            </p>
            <p className="text-gray-300 mb-4">Cookies are small data files that are stored on your device when you visit a website. We use Cookies to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>Authenticate your identity and maintain your logged-in state;</li>
              <li>Remember your preferences and settings;</li>
              <li>Analyze usage patterns and improve the functionality and performance of the Platform;</li>
              <li>Detect and prevent fraud and security risks;</li>
              <li>Provide customer support; and</li>
              <li>Deliver targeted content and marketing communications.</li>
            </ul>
            <p className="text-gray-300 mb-4">
              You may be able to refuse or disable Cookies by adjusting your browser settings. However, if you choose to
              disable Cookies, some features of the Platform and Services may not function properly or may be unavailable
              to you.
            </p>
            <p className="text-gray-300 mb-6">
              For more information about our use of Cookies and your choices, please review our Privacy Policy.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Collection and Use of Geolocation Data</h2>
            <p className="text-gray-300 mb-4">
              We may collect and use information about your geographic location through GPS, IP address, or other
              location-based technologies when you access or use the Platform and Services. We collect geolocation data
              to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>Verify your identity and compliance with eligibility requirements;</li>
              <li>Detect and prevent fraudulent activity and unauthorized access;</li>
              <li>Comply with applicable legal and regulatory requirements;</li>
              <li>Improve the functionality and security of the Platform and Services; and</li>
              <li>Provide location-specific features and content.</li>
            </ul>
            <p className="text-gray-300 mb-4">
              By using the Platform and Services, you consent to our collection, use, and sharing of your geolocation data
              as described in these Terms and our Privacy Policy. You may be able to disable location tracking through your
              device settings, but doing so may limit your ability to access certain features of the Services or may
              result in suspension or termination of your Namsoft account.
            </p>
            <p className="text-gray-300 mb-6">
              We reserve the right to deny access to the Platform and Services if we are unable to verify your location or
              if your location indicates that you are accessing the Services from a jurisdiction where such access is
              prohibited or restricted.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Restricted Activities</h2>
            <p className="text-gray-300 mb-4">
              In connection with your use of the Services or your Namsoft account or in the course of your interactions
              with us or third parties, you must not:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>Breach these Terms (or any policies or agreements incorporated herein) or any other agreements between you and us;</li>
              <li>Violate any law, statute, ordinance, or regulation (for example, those governing financial services, consumer protections, unfair competition, anti-discrimination or false advertising);</li>
              <li>Infringe our or any third party's copyright, patent, trademark, trade secret or other intellectual property rights, or rights of publicity or privacy;</li>
              <li>Create or control more than one personal account for yourself without our express authorization through, among other methods, using a name that is not yours, using a temporary email address or phone number, or providing any other falsified personal information;</li>
              <li>Provide false, inaccurate or misleading information;</li>
              <li>Refuse to cooperate in an investigation or provide confirmation of your identity or any information you provide to us;</li>
              <li>Control an account that is linked to another Namsoft account that has engaged in any of these restricted activities;</li>
              <li>Use the Services in a manner that results in or may result in complaints or disputes;</li>
              <li>Have any amounts owed to us;</li>
              <li>Take any action that imposes an unreasonable or disproportionately large load on our website, software, systems (including any networks and servers used to provide any of the Services) operated by us or on our behalf or the Services;</li>
              <li>Facilitate any viruses, trojan horses, malware, worms or other computer programming routines that attempts to or may damage, disrupt, corrupt, misuse, detrimentally interfere with, surreptitiously intercept or expropriate, or gain unauthorized access to any system, data, information or Services;</li>
              <li>Use an anonymizing proxy; use any robot, spider, other automatic device, or manual process to monitor or copy our website without our prior written permission; or use any device, software or routine to bypass our robot exclusion headers;</li>
              <li>Interfere or disrupt or attempt to interfere with or disrupt our website, software, systems (including any networks and servers used to provide any of the Services) operated by us or on our behalf, any of the Services or other users' use of any of the Services;</li>
              <li>Take any action that may cause us to lose any of the services from our Internet service providers, payment processors, or other suppliers or service providers;</li>
              <li>Use the Services to make excessive or unexplainable transactions;</li>
              <li>Circumvent any of our policies or determinations about your Namsoft account such as temporary or indefinite suspensions or other account holds, limitations or restrictions, including, but not limited to, engaging in the following actions: (a) attempting to open new or additional Namsoft account when your Namsoft account has been restricted, suspended or otherwise limited; (b) opening new or additional Namsoft accounts using information that is not your own (e.g. name, address, email address, etc.); or (c) using someone else's Namsoft account; or</li>
              <li>Harass and/or threaten our employees, agents, or other users.</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              Actions We May Take if You Engage in Any Restricted Activities
            </h2>
            <p className="text-gray-300 mb-4">
              If we believe that you've engaged in any restricted activities, we may take a number of actions to protect
              ourselves, our users and others at any time in our sole and absolute discretion. The actions we may take
              include, but are not limited to, the following:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>Terminate these Terms, limit your Namsoft account, and/or close or suspend your Namsoft account immediately and without penalty to us;</li>
              <li>Refuse to provide the Services to you in the future;</li>
              <li>Limit your access to our website, software, systems (including any networks and servers used to provide any of the Services) operated by us or on our behalf, your Namsoft account, or any of the Services;</li>
              <li>Update inaccurate information you provided us;</li>
              <li>Take legal action against you; or</li>
              <li>If you've violated our Acceptable Use Policy, hold you responsible for damages to us caused by your violation of this policy.</li>
            </ul>
            <p className="text-gray-300 mb-6">
              If we close your Namsoft account or terminate your use of the Services for any reason, we'll provide you
              with notice of our actions.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Account Limitations</h2>
            <p className="text-gray-300 mb-4">
              Limitations may be placed on your account to help protect Namsoft and Namsoft users when we notice restricted
              activities or activity that appears to us as unusual or suspicious. Limitations also help us collect
              information necessary for keeping your Namsoft account open.
            </p>
            <p className="text-gray-300 mb-4">
              There are several reasons why your Namsoft account could be limited, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>If we suspect someone could be using your Namsoft account without your knowledge, we'll limit it for your protection and look into the unusual activity.</li>
              <li>In order to comply with the law.</li>
              <li>If we reasonably believe you have breached these Terms or violated the Acceptable Use Policy or Content Standards.</li>
            </ul>
            <p className="text-gray-300 mb-6">
              You will need to resolve any issues with your account before a limitation can be removed. Normally, this is
              done after you provide us with the information we request. However, if we reasonably believe a risk still
              exists after you have provided us that information, we may take action to protect us, our users, a third
              party, or you from reversals, fees, fines, penalties, legal and/or regulatory risks and any other liability.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Communications Between You and Us</h2>
            <p className="text-gray-300 mb-4">
              By providing us your mobile phone number, you agree that we, including our affiliates, may contact you at
              that number using autodialed or prerecorded message calls or text messages to prevent fraud via multi-factor
              authentication. Message and data rates may apply. The frequency of messages may vary, and standard telephone
              minute and text charges may apply. Neither we nor your phone carriers are liable for delayed or undelivered
              messages. Please review our Privacy Policy in order to better understand our commitment to maintaining your
              privacy, as well as our use and disclosure of your information.
            </p>
            <p className="text-gray-300 mb-4">
              We may share your mobile phone number with service providers with whom we contract to assist us with the
              activities listed above, but we will not share your mobile phone number with third parties for their own
              purposes without your consent.
            </p>
            <p className="text-gray-300 mb-4">
              We may communicate with you about your Namsoft account and the Services electronically as described in our
              Consent to Receive Electronic Disclosures, which are attached as Exhibit B. You will be considered to have
              received a communication from us, if it's delivered electronically, 24 hours after the time we post it to
              our website or email it to you. You will be considered to have received a communication from us, if it's
              delivered by mail, 3 Business Days after we send it.
            </p>
            <p className="text-gray-300 mb-4">
              Unless you're communicating with us about a matter where we've specified another notice address. Written
              notices must be sent by postal mail to Namsoft Advisors, LLC, 135 7th Ave W, Suite 201/202, Kirkland, WA 98033.
            </p>
            <p className="text-gray-300 mb-6">
              You understand and agree that, to the extent permitted by law, we may, without further notice or warning,
              monitor or record telephone conversations or internet-based audio or video calls you or anyone acting on your
              behalf has with us or our agents for quality control and training purposes or for our own protection. You
              acknowledge and understand that while your communications with us may be overheard, monitored, or recorded
              not all telephone lines or calls, including internet-based audio or video calls, may be recorded by us, and
              we do not guarantee that recordings of any particular telephone calls will be retained or retrievable.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Our Rights</h2>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Our suspension and termination rights</h3>
            <p className="text-gray-300 mb-4">
              We, in our sole and absolute discretion, reserve the right to suspend or terminate these Terms, access to or
              use of our website, software, systems (including any networks and servers used to provide any of the
              Services) operated by us or on our behalf or some or all of the Services for any reason and at any time upon
              notice to you.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">No waiver</h3>
            <p className="text-gray-300 mb-6">
              Our failure to act with respect to a breach of any of your obligations under these Terms by you or others
              does not waive our right to act with respect to subsequent or similar breaches.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Limitation of Liability and Indemnification</h2>
            <p className="text-gray-300 mb-4">
              To the fullest extent permitted by applicable law, you understand and agree that Namsoft and the other
              Indemnified Persons will not be liable for any losses, liabilities, obligations, damages, claims, costs, and
              expenses (including, without limitation, any attorneys' fees or expenses) ("Losses") arising directly or
              indirectly out of (a) any breach of these Terms (including any exhibits attached hereto) by you; (b) any
              incorrect or inaccurate information or instructions provided by you to us; and (c) any acts or omissions of
              any third parties in connection with the operation of the Platform or the provision of the Services; provided
              that we acted with reasonable care in selecting such service providers.
            </p>
            <p className="text-gray-300 mb-4">
              Namsoft shall not be liable for delays or errors occurring by reason of circumstances beyond its control,
              including but not limited to acts of civil or military authority, national emergencies, work stoppages, fire,
              flood, catastrophe, acts of God, insurrection, war, riot, act of terrorism, or failure of communication or
              power supply. In the event of equipment breakdowns beyond its control, Namsoft shall take reasonable steps to
              minimize service interruptions but shall have no liability with respect thereto.
            </p>
            <p className="text-gray-300 mb-4">
              We may, from time to time, suspend access to your Namsoft account and/or the Services, for both scheduled and
              emergency maintenance.
            </p>
            <p className="text-gray-300 mb-4">
              Although we make reasonable efforts to update the information on the Platform, we make no representations,
              warranties or guarantees, whether express or implied, that the content on the Platform, including
              information in relation to the Services, is accurate, complete or up to date. Certain such information is
              provided by third parties, and Namsoft has not verified and assumes no responsibility for verifying the
              accuracy or completeness of such information and takes no responsibility for any inaccurate, incomplete, or
              out of date information provided by any third party.
            </p>
            <p className="text-gray-300 mb-4">
              Links to third-party website (including, without limitation, content, materials, and/or information in the
              third-party website) are not controlled by us. You acknowledge and agree that we are not responsible for any
              aspect of the content, materials, information or services contained in any third-party website accessible or
              linked from the Platform.
            </p>
            <p className="text-gray-300 mb-6">
              To the fullest extent permitted by applicable law, you agree to indemnify, defend, and hold harmless Namsoft
              and its affiliates and their respective owners, officers, directors, employees, contractors, representatives,
              and agents and their successors and assigns ("Indemnified Persons") from and against any and all Losses paid,
              suffered, or incurred by any Indemnified Person arising out of or in connection with (a) any breach of these
              Terms (including any exhibits attached hereto) by you; (b) any incorrect or inaccurate information or
              instructions provided by you to us; and (c) any acts or omissions of any third parties in connection with
              the operation of the Platform or the provision of the Services; provided that we acted with reasonable care
              in selecting such service providers.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Disclaimer of Warranty and Release</h2>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">No warranty</h3>
            <p className="text-gray-300 mb-4 uppercase text-sm">
              THE SERVICES ARE PROVIDED "AS-IS" AND WITHOUT ANY REPRESENTATION OR WARRANTY, WHETHER EXPRESS, IMPLIED OR
              STATUTORY. WE SPECIFICALLY DISCLAIM ANY IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-300 mb-6 uppercase text-sm">
              WE DO NOT GUARANTEE CONTINUOUS, UNINTERRUPTED OR SECURE ACCESS TO ANY PART OF THE SERVICES, AND OPERATION OF
              OUR WEBSITE, SOFTWARE, OR SYSTEMS (INCLUDING ANY NETWORKS AND SERVERS USED TO PROVIDE ANY OF THE SERVICES)
              OPERATED BY US OR ON OUR BEHALF MAY BE INTERFERED WITH BY NUMEROUS FACTORS OUTSIDE OF OUR CONTROL. WE WILL
              MAKE REASONABLE EFFORTS TO ENSURE THAT REQUESTS FOR ASSET TRANSFERS ARE PROCESSED IN A TIMELY MANNER BUT WE
              MAKE NO REPRESENTATIONS OR WARRANTIES REGARDING THE AMOUNT OF TIME NEEDED TO COMPLETE PROCESSING BECAUSE THE
              SERVICES ARE DEPENDENT UPON MANY FACTORS OUTSIDE OF OUR CONTROL, SUCH AS DELAYS IN THE BANKING SYSTEM OR THE
              INTERNET. SOME STATES DO NOT ALLOW THE DISCLAIMER OF IMPLIED WARRANTIES, SO THE FOREGOING DISCLAIMERS MAY
              NOT APPLY TO YOU.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Arbitration</h2>
            <p className="text-gray-300 mb-4">
              You and Namsoft agree that any claim or dispute at law or equity that has arisen or may arise between you
              and us (including claims or disputes that arise out of or relate to the Services or your Namsoft account)
              will be resolved in accordance with the Terms to Arbitrate provisions set forth below. Please read this
              information carefully. Among other things it:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>Affects your and our rights and will impact how claims between you and us are resolved, including your and our Terms to waive the right to trial by jury.</li>
              <li>Includes a Prohibition of Class and Representative Actions and Non-Individualized Relief pursuant to which you agree to waive your right to participate in a class action lawsuit against us.</li>
              <li>Requires you to follow the Opt-Out Procedure to opt-out of the Terms to Arbitrate by mailing us a written notice. If you are a new user, the opt-out notice must be postmarked no later than 30 days after the date you accept these Terms for the first time.</li>
            </ul>
            <p className="text-gray-300 mb-6">
              If a dispute arises between you and Namsoft regarding the Services, our goal is to learn about and address
              your concerns. If we are unable to do so to your satisfaction, we aim to provide you with a neutral and cost
              effective means of resolving the dispute quickly. Disputes between you and Namsoft regarding the Services may
              be reported to Namsoft at any time by contacting us at{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              .
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Terms to Arbitrate</h2>
            <p className="text-gray-300 mb-4">
              Any action or proceeding by you relating to any dispute must commence within one year after the cause of
              action accrues. Any dispute arising under or relating to this Agreement shall be submitted to binding
              arbitration in King County in the State of Washington pursuant to the rules for commercial arbitrations of
              the American Arbitration Association. Any arbitration award shall include an award of reasonable legal fees
              and costs to the prevailing party. Judgment upon the award rendered by the arbitrator may be entered in any
              court having jurisdiction. Notwithstanding the above, any action for injunctive relief shall be commenced in
              any court of competent jurisdiction located in King County in the State of Washington. The prevailing party
              in any such action for injunctive relief shall be entitled to an award of its reasonable legal fees and
              costs. Any dispute arising out of or related to the terms of this Agreement are personal to you and Namsoft
              and will be resolved solely through individual arbitration and will not be brought as a class arbitration,
              class action or any other type of representative proceeding. There will be no class arbitration or
              arbitration in which an individual attempts to resolve a dispute as a representative of another individual
              or group of individuals. Further, a dispute cannot be brought as a class or other type of representative
              action, whether within or outside of arbitration, or on behalf of any other individual or group of
              individuals.
            </p>
            <p className="text-gray-300 mb-4">
              Arbitration is more informal than a lawsuit in court. Arbitration uses a neutral arbitrator instead of a
              judge or jury, and court review of an arbitration award is very limited. However, the arbitrator can award
              the same damages and relief on an individual basis that a court can award to an individual. All issues are
              for the arbitrator to decide, except that a court of competent jurisdiction shall decide issues relating to
              arbitrability, the scope or enforceability of these Terms to Arbitrate, and issues that these Terms to
              Arbitrate indicates that a court can resolve.
            </p>
            <p className="text-gray-300 mb-4">
              You can choose to reject these Terms to Arbitrate ("opt-out") by mailing us a written opt-out notice. For new
              Namsoft users, the opt-out notice must be postmarked no later than 30 days after the date you accept the
              Terms for the first time. You must mail the opt-out notice to{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              .
            </p>
            <p className="text-gray-300 mb-6">
              Notwithstanding any provision in the Terms to the contrary, you and we agree that if we make any amendment
              to these Terms to Arbitrate (other than an amendment to any notice address or website link provided herein)
              in the future, that amendment shall not apply to any claim that was filed in a legal proceeding against
              Namsoft or you prior to the effective date of the amendment. The amendment shall apply to all other disputes
              or claims governed by these Terms to Arbitrate that have arisen or may arise between you and Namsoft. We will
              notify you of amendments to these Terms to Arbitrate by providing notice through email at least 21 days
              before the effective date of the amendments. If you do not agree to these amended terms, you may close your
              Namsoft account within the 21-day period and you will not be bound by the amended terms.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">Miscellaneous</h2>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Assignment</h3>
            <p className="text-gray-300 mb-4">
              You may not transfer or assign any rights or obligations you have under these Terms without our prior
              written consent. We may transfer or assign these Terms or any right or obligation under these Terms at any
              time.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Business Days</h3>
            <p className="text-gray-300 mb-4">
              "Business Day(s)" means Monday through Friday, excluding holidays when Namsoft offices are not considered
              open for business in the U.S. If a holiday falls on a Saturday, we observe the holiday on the prior Friday.
              If the holiday falls on a Sunday, we observe the holiday on the following Monday.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Governing law</h3>
            <p className="text-gray-300 mb-6">
              You agree that, except to the extent inconsistent with or preempted by federal law and except as otherwise
              stated in these Terms, the laws of the State of Washington, without regard to principles of conflict of
              laws, will govern these Terms and any claim or dispute that has arisen or may arise between you and Namsoft
              regarding your use of the Services.
            </p>

            {/* EXHIBIT A */}
            <div className="border-t border-white/[0.08] pt-8 mt-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">EXHIBIT A</h2>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4">ACCEPTABLE USE POLICY</h3>
              <p className="text-gray-500 mb-6">Last updated on December 22nd, 2025</p>

              <p className="text-gray-300 mb-4">
                Your use of any services offered by Namsoft Advisors, LLC and our affiliates ("Namsoft", "we", "us" or
                "our") is contingent on your full compliance with this Acceptable Use Policy. Capitalized terms used herein
                but not defined shall have the meaning ascribed to such terms in the Terms of Use, as amended from
                time-to-time. This policy may be modified and updated from time to time, and it is your responsibility to
                ensure that you periodically review this Acceptable Use Policy to ensure your full compliance with this
                policy. Your continued use of the Namsoft Platform and/or Services following any amendment of this
                Acceptable Use Policy constitutes your agreement to abide by the terms set forth herein. Your violation of
                this policy will constitute a material breach of any other agreement in connection with your use of any
                Namsoft services.
              </p>

              <p className="text-gray-300 mb-4">
                You are independently responsible for complying with all applicable laws in all of your actions related to
                your use of Namsoft's services, regardless of the purpose of the use. In addition, you may not use the
                Namsoft services (or allow the Namsoft services to be used) for activities that directly or indirectly:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>violate any law, statute, ordinance or regulation;</li>
                <li>involve money laundering or other activities designed to disguise the origin or use of funds;</li>
                <li>
                  relate to transactions involving (a) narcotics, steroids, certain controlled substances or other
                  products that present a risk to consumer safety, (b) drug paraphernalia, (c) cigarettes, (d) items that
                  encourage, promote, facilitate or instruct others to engage in illegal activity, (e) stolen goods
                  including digital and virtual goods, (f) the promotion of hate, violence, racial or other forms of
                  intolerance that is discriminatory or the financial exploitation of a crime, (g) items that are
                  considered obscene, (h) items that infringe or violate any copyright, trademark, right of publicity or
                  privacy or any other proprietary right under the laws of any jurisdiction, (i) sexually oriented
                  materials or services, (j) ammunition, firearms, or certain firearm parts or accessories, or (k) certain
                  weapons or knives regulated under applicable law;
                </li>
                <li>
                  relate to transactions that (a) show the personal information of third parties in violation of
                  applicable law, (b) support pyramid or ponzi schemes, matrix programs, other "get rich quick" schemes or
                  certain multi-level marketing programs, (c) are associated with purchases of annuities or lottery
                  contracts, lay-away systems, off-shore banking or transactions to finance or refinance debts funded by a
                  credit card, (d) are for the sale of certain items before the seller has control or possession of the
                  item, (e) are by payment processors to collect payments on behalf of merchants, (f) are associated with
                  the sale of traveler's checks or money orders, (g) involve foreign currency exchanges or check cashing
                  businesses, (h) involve certain credit repair, debt settlement services, credit transactions or
                  insurance activities, or (i) involve offering or receiving payments for the purpose of bribery or
                  corruption;
                </li>
                <li>
                  involve the sales of products or services identified by government agencies to have a high likelihood of
                  being fraudulent;
                </li>
                <li>allow others to gain access to your account or your user credentials;</li>
                <li>result in the provision of false or misleading information or instructions in connection with your use of the Services;</li>
                <li>
                  violate the security or integrity of any network, computer or communications system, software
                  application, or network or computing device (each, a "System"). Prohibited activities include but are
                  not limited to: (a) unauthorized access - accessing or using any System without permission, including
                  attempting to probe, scan, or test the vulnerability of a System or to breach any security or
                  authentication measures used by a System, (b) interception - monitoring of data or traffic on a System
                  without permission, (c) falsification of Origin - forging TCP-IP packet headers, e-mail headers, or any
                  part of a message describing its origin or route.
                </li>
              </ul>

              <p className="text-gray-300 mb-4">In addition, it is your responsibility to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>Ensure that all access credentials (including any usernames and passwords) in connection with your use of the Services are protected from unauthorized access;</li>
                <li>Immediately update any information provided by you to Namsoft that becomes inaccurate or incomplete;</li>
                <li>Comply with the policies and procedures of any third parties utilized by Namsoft in providing the Services; and</li>
                <li>Immediately notify Namsoft of any violation of this Acceptable Use Policy including providing details regarding such violation and provide assistance to Namsoft in addressing and remedying any violation.</li>
              </ul>

              <p className="text-gray-300 mb-4">
                Your failure to fully comply with this Acceptable Use Policy at all times could lead to suspension of your
                use of any Namsoft Services and liability for damages.
              </p>

              <p className="text-gray-300 mb-4">
                Namsoft may report any activity that it suspects violates any law or regulation to appropriate law
                enforcement officials, regulators, or other appropriate third parties without your knowledge. Reporting
                may include disclosure of your nonpublic personally identifiable information. Namsoft also may cooperate
                with appropriate law enforcement agencies, regulators, or other appropriate third parties to help with the
                investigation and prosecution of illegal conduct by providing related to alleged violations of this
                Policy.
              </p>

              <p className="text-gray-300 mb-6">
                You agree to indemnify, defend, and hold harmless Namsoft and its affiliates, and their respective
                principals, owners, directors, officers, employees, representatives, agents, and their respective
                successors and assigns (the "Namsoft Parties") from and against any actual or alleged claims, losses, taxes
                (including interest, penalties and additions to tax), damages, liabilities, awards, judgments and
                out-of-pocket costs and expenses (including reasonable attorneys' fees and expenses, the costs and expenses
                of any investigation and other costs and expenses incurred in connection with the defense of any actual or
                threatened action, proceeding or claim) arising out of or in connection with any violation of this
                Acceptable Use Policy by you.
              </p>
            </div>

            {/* EXHIBIT B */}
            <div className="border-t border-white/[0.08] pt-8 mt-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">EXHIBIT B</h2>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-6">Consent to Receive Electronic Disclosures</h3>

              <p className="text-gray-300 mb-4">
                By accepting this Consent as indicated below, you, the person or entity applying to utilize the Namsoft
                Platform and Services and to open a Namsoft account, consent to receive and view disclosures, notices,
                statements and other communications (collectively, "Disclosures") from Namsoft Advisors, LLC and our
                affiliates ("Namsoft", "we", "us" or "our") relating to your eligible Namsoft account(s) ("Account")
                electronically by any of the following means:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-1">
                <li>your email; or</li>
                <li>Notifications from or postings on our website located at www.runplutus.com.</li>
              </ul>

              <p className="text-gray-300 mb-4">
                Delivery by any of these means will constitute proper notice to you under applicable law. You acknowledge
                that Disclosures will include, but may not be limited to, the following:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-1">
                <li>The Namsoft Form ADV;</li>
                <li>The Namsoft website Privacy Policy, Terms of Use, and other Namsoft policies ("Policies and Agreements");</li>
                <li>Disclosures and/or amendments we may provide you under our Policies and Agreements;</li>
                <li>any other information on your Account;</li>
                <li>Periodic reports, receipts, confirmations, and authorizations and transaction history for your Account.</li>
                <li>Disclosures regarding the resolution of any claimed error on your periodic statements.</li>
              </ul>

              <p className="text-gray-300 mb-4">
                Your consent is effective until further notice by us or until you revoke your consent to receive
                electronic Disclosures. You may revoke your consent to receive electronic Disclosures at any time by
                submitting your request in writing to:{" "}
                <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                  HELLO@RUNPLUTUS.COM
                </a>
                .
              </p>

              <p className="text-gray-300 mb-4">
                If you do not consent or if you withdraw your consent, we reserve the right to refuse to accept your
                Account application, to cancel your Account, place your Account on inactive status, or to provide a paper
                copy of Disclosures. If you request a paper copy of a Disclosure within 180 days of the date of the
                Disclosure and we elect to send you a paper copy, we will waive our standard disclosure request Fee for the
                first two requests. After that, any additional disclosure requests may be subject to fees. We will only
                provide paper copies upon your request if your current mailing address is in your Account profile.
              </p>

              <p className="text-gray-300 mb-4">
                <strong>SYSTEM REQUIREMENTS:</strong> In order to receive Disclosures, whether by text or email, you need
                to have a means of printing or storing them. So, in addition to having an email address and phone number
                you must have the following:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-1">
                <li>Computer or mobile device with Internet connection;</li>
                <li>A current web browser with cookies enabled;</li>
                <li>A valid email address on file in your Account profile;</li>
                <li>Ability to store or print the Disclosures; and</li>
                <li>if you use a spam blocker, you must add{" "}
                  <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                    hello@runplutus.com
                  </a>{" "}
                  to your email address book or whitelist.
                </li>
              </ul>

              <p className="text-gray-300 mb-4">
                We reserve the right to change these system requirements and will provide you with a Disclosure when we
                make a material change to the system requirements.
              </p>

              <p className="text-gray-300 mb-4">
                In order to receive Disclosures, you must ensure that the primary mobile phone and/or email address that
                you provide us is your valid, current phone number or email address, and you are able to receive at that
                address texts or email messages containing Disclosures including attached electronic documents and that
                such Disclosures, including portions that are attached documents are available for viewing and storing or
                printing by you. You agree to promptly update your email address by updating your Account profile if your
                email address changes. You acknowledge that our ability to notify you of the availability of your
                Disclosures is contingent on the validity of mobile phone number and email address in our records.
              </p>

              <p className="text-gray-300 mb-4">
                If your mobile phone or email address is no longer valid, we reserve the right to determine your Account is
                inactive or take other actions as set forth in the User Agreement. You will not be able to conduct any
                transactions in your Account until you update your mobile phone or email address in your Account profile.
              </p>

              <p className="text-gray-300 mb-4">
                <strong>Reservation of Rights.</strong> We reserve the right to provide you with any Disclosure in
                writing, rather than electronically, or to withdraw the right to receive Disclosures electronically at any
                time. You agree to maintain on file with us your current street address and to promptly update your address
                in the event it changes by updating your Account profile. Although we may waive our fee for delivery of
                paper Disclosures, we reserve the right to charge the disclosure request Fee and to increase this fee in
                our discretion.
              </p>

              <p className="text-gray-300 mb-4">
                We recommend that you print a copy of this Consent and any Disclosure that you view electronically for
                your records as the Disclosure may not be accessible online at a later date.
              </p>

              <p className="text-gray-300 mb-4">
                By accepting the Terms of Use, you agree that (i) we may provide Disclosures to you electronically, on the
                terms and conditions set forth in this Consent, (ii) the consent shall last until you revoke your consent,
                and (iii) you meet the system requirements specified above. If you do not wish to receive Disclosures
                electronically, you will not be able to open an Account.
              </p>

              <p className="text-gray-300 mb-4">
                You understand and agree that we are responsible for sending the Disclosures to you electronically by email
                or text to the address in your Account profile or through the Service. We are not responsible for any delay
                or failure in your receipt of the email or text notices and whether or not you choose to view the
                Disclosure, subject to your right to revoke your consent to receive Disclosures electronically.
              </p>

              <p className="text-gray-300">
                <strong>Contact us.</strong> Except as otherwise provided by law or other agreement between you and us, you
                may provide us with notices regarding your Accounts by emailing us at{" "}
                <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                  hello@runplutus.com
                </a>{" "}
                or mailing us at Namsoft Advisors, LLC, 135 7th Ave W, Suite 201/202, Kirkland, WA 98033.
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-8 px-6 md:px-12 border-t border-white/[0.08]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-600">© {new Date().getFullYear()} Plutus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfUsePage;
